import type { AdminTab, Page } from '../types';

export const ADMIN_TABS: AdminTab[] = [
  'games',
  'players',
  'characters',
  'classes',
  'items',
  'spells',
  'rules',
  'admins',
  'npcs',
];

export const paths = {
  landing: '/',
  login: '/login',
  play: '/play',
  playJoin: (gameId: number) => `/play/join/${gameId}`,
  playSelect: (gameId: number) => `/play/select/${gameId}`,
  hero: '/hero',
  master: '/master',
  masterTab: (tab: AdminTab) => `/master/${tab}`,
  masterGames: '/master/games',
  masterGameNew: '/master/games/new',
  masterGame: (id: number) => `/master/games/${id}`,
  masterCombat: (id: number) => `/master/games/${id}/combat`,
};

export function isAdminTab(value: string | undefined): value is AdminTab {
  return !!value && (ADMIN_TABS as string[]).includes(value);
}

export function pageFromPath(pathname: string): Page {
  if (pathname === '/login' || pathname.startsWith('/login/')) return 'admin-login';
  if (/^\/master\/games\/\d+/.test(pathname)) return 'game-overview';
  if (pathname === '/master' || pathname.startsWith('/master/')) return 'admin-panel';
  if (pathname.startsWith('/play/select/')) return 'character-selection';
  if (pathname === '/hero' || pathname.startsWith('/hero/')) return 'character-sheet';
  if (pathname === '/play' || pathname.startsWith('/play/')) return 'player-view';
  return 'landing';
}

export function tabFromPath(pathname: string): AdminTab {
  const match = pathname.match(/^\/master\/([^/]+)/);
  const segment = match?.[1];
  if (isAdminTab(segment)) return segment;
  return 'games';
}

export function pathFromPage(
  page: Page,
  extras: { gameId?: number | null; tab?: AdminTab } = {}
): string {
  switch (page) {
    case 'admin-login':
      return paths.login;
    case 'admin-panel':
      return paths.masterTab(extras.tab || 'games');
    case 'game-overview':
      return extras.gameId ? paths.masterGame(extras.gameId) : paths.masterGames;
    case 'player-view':
      return paths.play;
    case 'character-selection':
      return extras.gameId ? paths.playSelect(extras.gameId) : paths.play;
    case 'character-sheet':
      return paths.hero;
    default:
      return paths.landing;
  }
}

export function parseGameIdFromPath(pathname: string): number | null {
  const master = pathname.match(/^\/master\/games\/(\d+)/);
  if (master) return parseInt(master[1], 10);
  const select = pathname.match(/^\/play\/select\/(\d+)/);
  if (select) return parseInt(select[1], 10);
  const join = pathname.match(/^\/play\/join\/(\d+)/);
  if (join) return parseInt(join[1], 10);
  return null;
}
